const plugin = ({ $store, $recipe }) => {
  return {
    install(Vue) {
      Vue.prototype.$ugc = {
        /**
         * @function returnBucketSize
         * @return {string} - bucketSize - ex: '9.0'
         * Find the bucket size in any of the places it can be stored and return it.
         */
        returnBucketSize(componentName) {
          const { tempData } = $store.state.ugc;
          const { bucketSizes, pdm } = $store.state.utils;
          let bucketSize;
          if (tempData.features[tempData.activeUGCIndex] && tempData.features[tempData.activeUGCIndex].bucketSize) {
            bucketSize = tempData.features[tempData.activeUGCIndex].bucketSize;
          } else if (bucketSizes) {
            let ugcSize;
            if ($store.getters["ugc/storageController/IS_IN_STORAGE"](componentName)) {
              ugcSize = $store.getters["ugc/storageController/GET_SIZE"](componentName);
            } else {
              ugcSize = $recipe.getUGCSizeFromRecipe();
            }
            bucketSize = $store.state.utils.bucketSizes[ugcSize];
          }

          // If no bucket size is found, return default value from PDM
          if (!bucketSize && pdm) {
            bucketSize = bucketSizes[pdm.ugcDefaultSize];
          }

          return bucketSize;
        },

        /**
         * @function hasUGCFromSwatch
         * @return {boolean} - hasUgcFromSwatch - Recipe has ugc as a swatch
         * Checks the UGC enabled components and looks in the recipe for any ugc as a swatch applied.
         */
        hasUGCFromSwatch() {
          const ugcComponents = $store.getters['utils/isApparelEnabled']
            ? $store.getters["ugc/getApparelUGCEnabledComponents"]
            : $store.getters["ugc/getUGCEnabledComponents"];
          let hasUgcFromSwatch = false;

          if (ugcComponents.length) {
            for (let i = 0; i < ugcComponents.length; i++) {
              if (this.isUGCFromSwatch($store.state.recipe.recipe_v2.components[ugcComponents[i]].swatch_id)) {
                hasUgcFromSwatch = true;
                break;
              }
            }
          }
          return hasUgcFromSwatch;
        },

        /**
         * @function hasUGCFromSwatch
         * @param {string} - swatchId - Id of the swatch to check
         * @return {boolean} - hasUgcFromSwatch - Is ugc from swatch
         * Checks the swatch id for the ugc object.
         */
        isUGCFromSwatch(swatchId) {
          return $store.getters["swatches/isUGCFromSwatch"]({ swatchId });
        },

        /**
         * @function isUGCCreatedByFeature
         * @param {Object} - recipeObj - UGC recipe object
         * @param {Boolean} includePatternMaker Flag to consider patternMaker in the search
         * @param {Boolean} includeCustomCheckerboard Flag to consider customCheckerboard in the search
         * @returns {Boolean} - Returns true if the swatch ugc created
         */
        isUGCCreatedByFeature(recipeObj, { includePatternMaker = false, includeCustomCheckerboard = false, includeImageLibrary = false }) {
          if (!includePatternMaker && !includeCustomCheckerboard && !includeImageLibrary) return false;
          if (includeImageLibrary) {
            if (recipeObj && recipeObj.subgroup === "library") return true;
          }
          if (includePatternMaker) {
            if (recipeObj && recipeObj.subgroup === "checkerboard-creator") return true;
          }
          if (includeCustomCheckerboard) {
            if (recipeObj && recipeObj.subgroup === "custom-checkerboard") return true;
          }
          return false;
        },

        /**
         * @function getComponentsWithUgcApplied
         * @param {boolean} - ugcSwatchCheck - include UGC as a swatch in the search
         * @returns {array} - Returns an array with the componets that have ugc applied
         */
        getComponentsWithUgcApplied(ugcSwatchCheck) {
          let ugcComponents = $store.getters["ugc/getUGCEnabledComponents"];
          if ($store.getters['utils/isApparelEnabled']) {
            ugcComponents = $store.getters["ugc/getApparelUGCEnabledComponents"];
          }
          const componentsWithUgcApplied = [];

          if (ugcComponents.length) {
            ugcComponents.forEach(component => {
              const componentHasUgc = $recipe.checkForUGCComponent(component);
              const componentRecipe = $store.state.recipe.recipe_v2.components[component];
              const hasUgcSwatch = this.isUGCFromSwatch(componentRecipe.swatch_id);
              const hasPatternOrCustomCheckerboard = this.isUGCCreatedByFeature(componentRecipe, {
                includePatternMaker: true,
                includeCustomCheckerboard: true,
              });
              if (typeof ugcSwatchCheck === "undefined" && (hasUgcSwatch || componentHasUgc)) {
                componentsWithUgcApplied.push(component);
                return;
              }

              // Gets components with regular ugc or swatch ugc depending on ugcSwatchCheck boolean
              if (
                (ugcSwatchCheck && (hasUgcSwatch || hasPatternOrCustomCheckerboard)) ||
                (!ugcSwatchCheck && !hasPatternOrCustomCheckerboard && !hasUgcSwatch && componentHasUgc)
              ) {
                componentsWithUgcApplied.push(component);
              }
            });
          }
          return componentsWithUgcApplied;
        },

        /**
         * @function getActiveUgcSize
         * @returns {Object} - returns the current ugc size
         */
        getActiveUgcSize() {
          const components = this.getComponentsWithUgcApplied(false);
          if ($store.state.utils.configuration.ugc.disableSizeSelector && $store.state.utils.configuration.ugc.defaultRecipeSize) return $store.state.utils.configuration.ugc.defaultRecipeSize;
          return components.length ? $store.state.recipe.recipe_v2.components[components[0]].ugc.size : false;
        },
        /**
         * @function getImageScale
         * @param {Object} overlaySVG, overlay from fabricCanvas
         * @param {Object} img, image into the fabricCanvas
         * get the scale size image when the placement is bigger than the placement image
         */
        getImageScale(overlaySVG, img) {
          if(!img && !overlaySVG) return;
          const extrasize = 3;
          const imgSVGHeigth = overlaySVG.height * overlaySVG.scaleY;
          const imgSVGWidth = overlaySVG.width * overlaySVG.scaleX;
          const imgHeigth = img.height * img.scaleY;
          const imgWidth = img.width * img.scaleX;
          let scale = img.scaleX;

          if(imgHeigth < imgSVGHeigth) {
            scale = (imgSVGHeigth + extrasize)/img.height;
          } else  if (imgWidth < imgSVGWidth) {
            scale = (imgSVGWidth + extrasize)/img.width;
          }

          return scale;
        }
      };
    }
  };
};

export default plugin;
